import '~/utilities/polyfills/Object.groupBy';

import type { AppProps } from 'next/app';

import { ApolloProvider } from '@apollo/client';
import { StyleSheetManager, ThemeProvider } from 'styled-components';

import { Favicons } from '~/components/shared/core/icons/favicons/Favicons';
import { PreloadFonts } from '~/components/shared/core/typography/PreloadFonts';
import { Botify } from '~/components/shared/vendors/Botify';
import { GlobalStyle } from '~/core/theme/globalStyle';
import type { CommonPageProps } from '~/types/props';
import { PageTypes } from '~/utilities/constants';
import { CommonContextList } from '~/utilities/context/CommonContextList';
import { AppProvider } from '~/utilities/context/static/AppContext';
import { getClient } from '~/utilities/graphql/apolloClient';
import type { SiteType } from '~/utilities/graphql/codegen';
import { useLangObserver } from '~/utilities/hooks';
import { ExponeaPersonalizationSync } from '~/utilities/hooks/search/ExponeaPersonalizationSync';
import { useBodyDataAttributes } from '~/utilities/hooks/useBodyDataAttributes';
import { useExternalDialogFocusTrap } from '~/utilities/hooks/useExternalDialogFocusTrap';
import { usePageviewCount } from '~/utilities/hooks/usePageviewCount';
import { toHtmlLang } from '~/utilities/parsers';
import { getSiteTypeByHostname } from '~/utilities/parsers/getSiteTypeByHostname';

if (typeof globalThis.window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (async () => {
    const React = await import('react');
    const ReactDOM = await import('react-dom');
    const axe = await import('@axe-core/react');
    const { config, options } = await import('../config/axe');

    axe.default(React.default, ReactDOM.default, 1000, {
      ...config,
      ...options,
    });
  })();
}

function App({
  Component,
  pageProps,
  router,
}: AppProps<CommonPageProps | { allSiteTypeProps: Record<SiteType, CommonPageProps> }>) {
  const hasAllSiteTypeProps = 'allSiteTypeProps' in pageProps;

  let siteType: SiteType | undefined;

  if (hasAllSiteTypeProps) {
    const hostName = globalThis?.location?.host;

    siteType = getSiteTypeByHostname(hostName);
  } else {
    siteType = pageProps.siteType;
  }

  const { locale = 'en' } = router;
  const apolloClient = getClient();

  useLangObserver(toHtmlLang(locale));
  useExternalDialogFocusTrap();
  usePageviewCount();
  useBodyDataAttributes(
    hasAllSiteTypeProps ? siteType && pageProps.allSiteTypeProps[siteType] : pageProps
  );

  if (!siteType) {
    return <></>;
  }

  const props = hasAllSiteTypeProps ? pageProps.allSiteTypeProps[siteType] : pageProps;

  const { key, ...restProps } = props || {};
  const { botifyEnabled, botifyId } = props?.configuration || {};
  const { pageType } = props || { pageType: PageTypes.OTHER };

  return (
    <ApolloProvider client={apolloClient}>
      <AppProvider locale={locale} siteType={siteType} pageType={pageType}>
        <Botify enabled={botifyEnabled} instanceId={botifyId} />
        <PreloadFonts />
        <Favicons siteType={siteType} />
        <StyleSheetManager enableVendorPrefixes>
          <ThemeProvider theme={props?.theme || {}}>
            <GlobalStyle />
            <CommonContextList
              configuration={props?.configuration || {}}
              countryEn={props?.countryEn || {}}
              topLevelNavigationContent={props?.topLevelNavigationContent}
            >
              <ExponeaPersonalizationSync configuration={props?.configuration || {}} />
              <Component key={key} {...restProps} />
            </CommonContextList>
          </ThemeProvider>
        </StyleSheetManager>
      </AppProvider>
    </ApolloProvider>
  );
}

export default App;
